/**
 * Domain Error Classes for API Layer
 *
 * 에러 타입을 분류하여 UI에서 적절한 처리가 가능하도록 함
 * - UnsupportedDocumentTypeError: 개발자 버그 (미지원 문서 타입)
 * - ValidationError: 사용자 입력 오류 (필드별 에러 메시지)
 * - ServerError: 서버 오류 (4xx/5xx)
 * - NetworkError: 네트워크 오류 (연결 실패)
 */

/**
 * Base class for all API domain errors
 */
export abstract class ApiError extends Error {
  abstract readonly code: string;
  abstract readonly isRetryable: boolean;

  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
    // Maintains proper stack trace for where error was thrown
    // Node.js 환경에서 스택 트레이스 최적화
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (typeof (Error as any).captureStackTrace === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      (Error as any).captureStackTrace(this, this.constructor);
    }
  }
}

/**
 * Field-level validation error from server
 * Maps to form field errors in UI
 */
export interface FieldError {
  field: string;
  message: string;
  code?: string;
}

export class ValidationError extends ApiError {
  readonly code = 'VALIDATION_ERROR';
  readonly isRetryable = false;
  readonly fields: FieldError[];

  constructor(message: string, fields: FieldError[] = []) {
    super(message);
    this.fields = fields;
  }

  /**
   * Convert to react-hook-form compatible error object
   */
  toFormErrors(): Record<string, { message: string }> {
    return this.fields.reduce(
      (acc, { field, message }) => {
        acc[field] = { message };
        return acc;
      },
      {} as Record<string, { message: string }>,
    );
  }
}

/**
 * Server-side error (4xx/5xx responses)
 */
export class ServerError extends ApiError {
  readonly code = 'SERVER_ERROR';
  readonly isRetryable: boolean;
  readonly statusCode: number;
  readonly serverMessage?: string;
  readonly errorCode?: string;

  constructor(
    statusCode: number,
    message: string,
    options?: { serverMessage?: string; errorCode?: string },
  ) {
    super(message);
    this.statusCode = statusCode;
    this.serverMessage = options?.serverMessage;
    this.errorCode = options?.errorCode;
    // 5xx errors are retryable, 4xx are not (except 429)
    this.isRetryable = statusCode >= 500 || statusCode === 429;
  }

  /**
   * Check if this is a client error (4xx)
   */
  isClientError(): boolean {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  /**
   * Check if this is a server error (5xx)
   */
  isServerError(): boolean {
    return this.statusCode >= 500;
  }
}

/**
 * Network-level error (connection failed, timeout, etc.)
 */
export class NetworkError extends ApiError {
  readonly code = 'NETWORK_ERROR';
  readonly isRetryable = true;
  readonly originalError?: Error;

  constructor(message: string, originalError?: Error) {
    super(message);
    this.originalError = originalError;
  }
}

/**
 * 백엔드 401 응답의 code 필드 상수
 */
export const AUTH_ERROR_CODES = {
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/**
 * Type guard functions
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

export function isServerError(error: unknown): error is ServerError {
  return error instanceof ServerError;
}

/**
 * Convert axios error to domain error
 */
export function mapAxiosError(error: unknown): ApiError {
  // Already a domain error
  if (isApiError(error)) {
    return error;
  }

  // Check if it's an axios error
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as unknown as {
      response?: {
        status: number;
        data?: { errorMessage?: string; message?: string; errors?: FieldError[]; code?: string };
      };
      message: string;
      code?: string;
    };

    // No response - network error
    if (!axiosError.response) {
      return new NetworkError(
        axiosError.code === 'ECONNABORTED'
          ? '요청 시간이 초과되었습니다.'
          : '네트워크 연결에 실패했습니다.',
        error instanceof Error ? error : undefined,
      );
    }

    const { status, data } = axiosError.response;
    const serverMessage = data?.errorMessage || data?.message;

    // Validation error (typically 400 with field errors)
    if (status === 400 && data?.errors && Array.isArray(data.errors)) {
      return new ValidationError(serverMessage || '입력값을 확인해주세요.', data.errors);
    }

    // Unauthorized — code별 메시지 분기
    if (status === 401) {
      const errorCode = data?.code;
      let message: string;
      if (errorCode === AUTH_ERROR_CODES.TOKEN_EXPIRED) {
        message = '세션이 만료되었습니다. 다시 로그인해주세요.';
      } else if (errorCode === AUTH_ERROR_CODES.INVALID_TOKEN) {
        message = '인증 정보가 유효하지 않습니다. 다시 로그인해주세요.';
      } else {
        message = '인증이 필요합니다. 다시 로그인해주세요.';
      }
      return new ServerError(status, message, { serverMessage, errorCode });
    }

    // Forbidden
    if (status === 403) {
      return new ServerError(status, '접근 권한이 없습니다.', { serverMessage });
    }

    // Not found
    if (status === 404) {
      return new ServerError(status, '요청한 리소스를 찾을 수 없습니다.', { serverMessage });
    }

    // Other client errors
    if (status >= 400 && status < 500) {
      return new ServerError(status, serverMessage || '요청을 처리할 수 없습니다.', {
        serverMessage,
      });
    }

    // Server errors
    if (status >= 500) {
      return new ServerError(status, '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', {
        serverMessage,
      });
    }
  }

  // Fallback for unknown errors
  const message = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
  return new ServerError(500, message);
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return '알 수 없는 오류가 발생했습니다.';
}
